import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getUser, logout } from '../../services/helpers'
import { listCenterOrders } from '../../services/orders'
import {
  ChartBarIcon,
  CalendarIcon,
  PlusCircleIcon,
  TagIcon,
  ShoppingBagIcon,
  TicketIcon,
  WalletIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  BuildingOfficeIcon,
  Bars3Icon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  CreditCardIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  QrCodeIcon
} from '@heroicons/react/24/outline'

const CenterOrdersPage = () => {
  const navigate = useNavigate()
  const [user, setUser] = useState(getUser())
  const displayName = user?.organization || user?.name || 'Event Center'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('ALL')
  const [searchQuery, setSearchQuery] = useState('')

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon, path: '/center' },
    { id: 'events', label: 'My Events', icon: CalendarIcon, path: '/center/events', disabled: true },
    { id: 'offers', label: 'My Offers', icon: TagIcon, path: '/center/offers' },
    { id: 'orders', label: 'Orders', icon: ShoppingBagIcon, path: '/center/orders' },
    { id: 'tickets', label: 'Tickets', icon: TicketIcon, path: '/center/tickets' },
    { id: 'verify', label: 'Verify Tickets', icon: QrCodeIcon, path: '/center/verify' },
    { id: 'media', label: 'Media Gallery', icon: PhotoIcon, path: '/center/media', disabled: true },
    { id: 'disputes', label: 'Disputes', icon: ExclamationTriangleIcon, path: '/center/disputes' },
    { id: 'wallet', label: 'Wallet', icon: WalletIcon, path: '/center/wallet' },
    { id: 'profile', label: 'Profile', icon: UserCircleIcon, path: '/center/profile' }
  ]

  useEffect(() => {
    fetchOrders()
  }, [])

  const fetchOrders = async () => {
    setLoading(true)
    const res = await listCenterOrders()
    if (res.status === 200) {
      setOrders(res.data || [])
    }
    setLoading(false)
  }

  const filteredOrders = orders.filter(order => {
    // Filter by status
    let passesFilter = true
    if (filter !== 'ALL') {
      passesFilter = order.status === filter
    }

    // Filter by search query
    if (searchQuery && passesFilter) {
      const query = searchQuery.toLowerCase()
      const matchesOrderId = order._id?.toLowerCase().includes(query) ||
                             order.paymentTxRef?.toLowerCase().includes(query)
      const matchesCustomer = order.attendeeId?.name?.toLowerCase().includes(query) ||
                              order.attendeeId?.email?.toLowerCase().includes(query)
      const matchesEvent = order.eventId?.title?.toLowerCase().includes(query)
      const matchesOffer = order.offerId?.title?.toLowerCase().includes(query)

      return matchesOrderId || matchesCustomer || matchesEvent || matchesOffer
    }

    return passesFilter
  })

  const completedOrders = orders.filter(o => o.status === 'TICKET_ISSUED' || o.status === 'PAID')
  const totalRevenue = completedOrders.reduce((sum, o) => sum + (o.total || 0), 0)

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {/* Sidebar Navigation */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-100">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center text-white shadow-sm">
              <BuildingOfficeIcon className="w-5 h-5" />
            </div>
            <span className="text-xl font-bold text-slate-900 tracking-tight">Obule Panel</span>
            <button onClick={() => setMobileMenuOpen(false)} className="ml-auto lg:hidden">
              <XMarkIcon className="w-6 h-6 text-slate-400" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {tabs.filter(tab => !tab.disabled).map((tab) => {
              const Icon = tab.icon
              const isActive = tab.id === 'orders'

  return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                  {tab.label}
                </Link>
              )
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                <UserCircleIcon className="w-6 h-6" />
              </div>
              <div className="overflow-hidden">
                <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={() => {
                logout()
                navigate('/center/login')
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 lg:ml-64 min-w-0 flex flex-col">
        {/* Top Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 flex items-center justify-between px-4 h-16 sticky top-0 z-40">
          <button onClick={() => setMobileMenuOpen(true)}>
            <Bars3Icon className="w-6 h-6 text-slate-600" />
          </button>
          <span className="font-semibold text-slate-900">Orders</span>
          <div className="w-6" />
        </header>

        {/* Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900">Orders</h1>
            <p className="text-slate-600 mt-1">View and manage all customer orders</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="text-emerald-100 text-sm font-medium">Total Orders</p>
                <ShoppingBagIcon className="w-8 h-8 text-white/30" />
              </div>
              <p className="text-3xl font-bold">{orders.length}</p>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="text-green-100 text-sm font-medium">Completed</p>
                <CheckCircleIcon className="w-8 h-8 text-white/30" />
              </div>
              <p className="text-3xl font-bold">{completedOrders.length}</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="text-yellow-100 text-sm font-medium">Pending</p>
                <ClockIcon className="w-8 h-8 text-white/30" />
              </div>
              <p className="text-3xl font-bold">{orders.filter(o => o.status === 'INITIATED').length}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="text-purple-100 text-sm font-medium">Total Revenue</p>
                <CreditCardIcon className="w-8 h-8 text-white/30" />
              </div>
              <p className="text-3xl font-bold">₦{totalRevenue.toLocaleString()}</p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by order ID, customer name, email, event or offer..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="mt-2 text-sm text-slate-600">
                Found {filteredOrders.length} order{filteredOrders.length !== 1 ? 's' : ''} matching "{searchQuery}"
              </p>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-6">
            {['ALL', 'INITIATED', 'PAID', 'TICKET_ISSUED', 'CANCELLED', 'FAILED'].map(status => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  filter === status
                    ? 'bg-emerald-600 text-white shadow-lg'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {status}
              </button>
            ))}
          </div>

          {/* Orders Table */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-emerald-600 border-r-transparent"></div>
                <p className="mt-4 text-slate-600">Loading orders...</p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-4 text-left">Order ID</th>
                      <th className="px-6 py-4 text-left">Type</th>
                      <th className="px-6 py-4 text-left">Event/Offer</th>
                      <th className="px-6 py-4 text-left">Date</th>
                      <th className="px-6 py-4 text-left">Customer</th>
                      <th className="px-6 py-4 text-left">Items</th>
                      <th className="px-6 py-4 text-left">Amount</th>
                      <th className="px-6 py-4 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredOrders.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-16 text-center">
                          <ShoppingBagIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                          <p className="text-slate-500 font-medium">No orders found</p>
                          <p className="text-slate-400 text-sm mt-1">Orders will appear here when customers make purchases</p>
                        </td>
                      </tr>
                    ) : (
                      filteredOrders.map(order => (
                        <tr key={order._id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4">
                            <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded text-slate-700">
                              #{order.paymentTxRef?.slice(-8) || order._id.slice(-8)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                              order.orderType === 'EVENT'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-emerald-100 text-emerald-700'
                            }`}>
                              {order.orderType || 'EVENT'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="max-w-xs">
                              <p className="text-slate-900 font-medium truncate">
                                {order.eventId?.title || order.offerId?.title || 'N/A'}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-slate-600">
                            <div>
                              <p>{new Date(order.createdAt).toLocaleDateString()}</p>
                              <p className="text-xs text-slate-400">
                                {new Date(order.createdAt).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-slate-900 font-medium">{order.attendeeId?.name || 'Guest'}</p>
                              <p className="text-xs text-slate-500">{order.attendeeId?.email || ''}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-slate-600">
                            {order.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0} ticket(s)
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-slate-900 font-semibold">
                              {order.currency || 'NGN'} {order.total?.toLocaleString()}
                            </p>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                              order.status === 'TICKET_ISSUED' || order.status === 'PAID'
                                ? 'bg-green-100 text-green-700 border border-green-200'
                                : order.status === 'INITIATED'
                                ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                                : order.status === 'CANCELLED'
                                ? 'bg-gray-100 text-gray-700 border border-gray-200'
                                : 'bg-red-100 text-red-700 border border-red-200'
                            }`}>
                              {order.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
    </main>
      </div>
    </div>
  )
}

export default CenterOrdersPage
